import { useEffect, useState } from 'react';
import { Badge, Button, Card, CardHeader, Collapse, Table } from 'reactstrap';
import { toast } from 'react-toastify';

// While a report is building, re-check this often. Each check also lets the
// backend relaunch a report whose worker died.
const POLL_MS = 30000;

const formatDay = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

const statusBadge = (report) => {
  if (report.status === 'queued' || report.status === 'running') {
    const progress = report.users
      ? ` (${report.usersFinished || 0}/${report.users} users)`
      : '';
    return <Badge color="info">Building{progress}</Badge>;
  }
  if (report.status === 'failed') return <Badge color="danger">Failed</Badge>;
  if (!report.downloadable) return <Badge color="secondary">Expired</Badge>;
  return <Badge color="success">Ready</Badge>;
};

/**
 * Background Adoption reports: the dashboard's data built on the server for
 * teams or ranges too large to load here, emailed when ready and kept 30 days.
 * The dashboard's Generate Report / Refresh Data buttons start them and bump
 * reloadKey. A report is a .cpta file, so "View" loads it exactly like
 * Import .cpta.
 */
const AdoptionReports = ({ apiClient, reloadKey, onViewReport }) => {
  const [reports, setReports] = useState([]);
  const [isOpen, setIsOpen] = useState(true);
  const [busyJobId, setBusyJobId] = useState(null);

  const loadReports = async () => {
    try {
      const data = await apiClient.listAdoptionReports();
      if (data?.reports) setReports(data.reports);
    } catch (error) {
      console.error('Error loading adoption reports:', error);
    }
  };

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const building = reports.some(
    (r) => r.status === 'queued' || r.status === 'running',
  );
  useEffect(() => {
    if (!building) return undefined;
    const timer = setTimeout(loadReports, POLL_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports]);

  const getLink = async (report) => {
    const data = await apiClient.getAdoptionReportDownload(report.jobId);
    if (!data?.url) throw new Error(data?.error || 'Report unavailable');
    return data;
  };

  const handleDownload = async (report) => {
    setBusyJobId(report.jobId);
    try {
      const { url } = await getLink(report);
      const a = document.createElement('a');
      a.href = url;
      a.click();
    } catch (error) {
      toast.error('This report is no longer available.');
      loadReports();
    } finally {
      setBusyJobId(null);
    }
  };

  const handleView = async (report) => {
    setBusyJobId(report.jobId);
    try {
      const { url, filename } = await getLink(report);
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      onViewReport(await response.json(), filename);
    } catch (error) {
      console.error('Error viewing adoption report:', error);
      toast.error('Could not open this report.');
    } finally {
      setBusyJobId(null);
    }
  };

  return (
    <Card style={{ marginBottom: '20px' }}>
      <CardHeader
        onClick={() => setIsOpen(!isOpen)}
        style={{
          cursor: 'pointer',
          backgroundColor: '#f7fafc',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>🗂 Recent Reports</h4>
          <small style={{ color: '#6c757d' }}>
            Reports are built in the background for the selected date range and
            labels. We&apos;ll email you when one is ready. Reports are kept for
            30 days.
          </small>
        </div>
        <i className={`fa fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
      </CardHeader>
      <Collapse isOpen={isOpen}>
        {reports.length > 0 && (
          // A long history scrolls inside the card instead of lengthening the page.
          <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
            <Table size="sm" style={{ marginBottom: 0 }}>
              <thead
                style={{
                  position: 'sticky',
                  top: 0,
                  background: '#fff',
                  zIndex: 1,
                }}
              >
                <tr>
                  <th>Requested</th>
                  <th>Date range</th>
                  <th>Labels</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr key={report.jobId}>
                    <td>{new Date(report.createdAt).toLocaleString()}</td>
                    <td>
                      {formatDay(report.dateRange?.startDate)} –{' '}
                      {formatDay(report.dateRange?.endDate)}
                    </td>
                    <td>
                      {report.labels.length
                        ? report.labels.join(', ')
                        : 'All users'}
                    </td>
                    <td>
                      {statusBadge(report)}{' '}
                      {report.failedUsers.length > 0 && (
                        <Badge
                          color="warning"
                          title={report.failedUsers
                            .map((u) => u.email)
                            .join(', ')}
                        >
                          {report.failedUsers.length} user
                          {report.failedUsers.length === 1 ? '' : 's'} failed
                        </Badge>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {report.downloadable && (
                        <>
                          <Button
                            color="link"
                            size="sm"
                            onClick={() => handleView(report)}
                            disabled={busyJobId === report.jobId}
                          >
                            View
                          </Button>
                          <Button
                            color="link"
                            size="sm"
                            onClick={() => handleDownload(report)}
                            disabled={busyJobId === report.jobId}
                          >
                            Download
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Collapse>
    </Card>
  );
};

export default AdoptionReports;
